import type { Block, Localized, ManualCategory, ManualDoc } from "../manual/types.ts"

/**
 * 매뉴얼을 찾고 훑는 규칙. 화면(`pages/ManualPage.tsx`)은 여기 결과만 그린다 —
 * JSX 안 조건식으로 두면 테스트할 수 없다.
 */
export type Locale = "ko" | "ja"

/** 한 블록에서 글자만 뽑는다(검색용). */
function blockText(block: Block, locale: Locale): string {
  switch (block.kind) {
    case "p":
    case "heading":
    case "note":
      return block.text[locale]
    case "steps":
    case "list":
      return block.items.map((item) => item[locale]).join(" ")
    case "image":
      // 사진 설명도 검색에 걸리게 한다 — 그림 속 글자는 찾을 수 없으므로.
      return [block.alt[locale], block.caption?.[locale] ?? ""].join(" ")
    case "table":
      return [...block.head, ...block.rows.flat()].map((cell) => cell[locale]).join(" ")
  }
}

/** 제목·요약·본문·낱말을 한 덩어리로. 본문까지 검색되게 하는 것이 요점이다. */
export function docText(doc: ManualDoc, locale: Locale): string {
  return [
    doc.title[locale],
    doc.summary[locale],
    doc.keywords.join(" "),
    ...doc.body.map((block) => blockText(block, locale)),
  ]
    .join(" ")
    .toLowerCase()
}

export type SearchHit = { doc: ManualDoc; snippet: string }

/**
 * 검색.
 *
 * 띄어쓰기로 나눈 낱말이 **모두** 들어 있는 문서를 찾는다 — "반품 등록"처럼 두 낱말로 치면
 * 둘 다 있는 문서만 나온다. 찾은 자리 앞뒤를 잘라 미리보기로 준다.
 */
export function searchDocs(
  docs: readonly ManualDoc[],
  query: string,
  locale: Locale,
): SearchHit[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return []

  const hits: SearchHit[] = []
  for (const doc of docs) {
    const text = docText(doc, locale)
    if (!tokens.every((token) => text.includes(token))) continue
    hits.push({ doc, snippet: snippetAround(doc, tokens[0], locale) })
  }
  return hits
}

const SNIPPET_BEFORE = 24
const SNIPPET_AFTER = 56

/** 찾은 낱말 둘레의 본문 한 조각. 없으면 요약을 그대로 쓴다. */
export function snippetAround(doc: ManualDoc, token: string, locale: Locale): string {
  const plain = [doc.summary[locale], ...doc.body.map((block) => blockText(block, locale))].join(" ")
  const at = plain.toLowerCase().indexOf(token)
  if (at === -1) return doc.summary[locale]

  const from = Math.max(0, at - SNIPPET_BEFORE)
  const to = Math.min(plain.length, at + token.length + SNIPPET_AFTER)
  return `${from > 0 ? "…" : ""}${plain.slice(from, to).trim()}${to < plain.length ? "…" : ""}`
}

export type DocGroup = { category: ManualCategory; docs: ManualDoc[] }

/** 분류별로 묶어 차례대로. 문서가 하나도 없는 분류는 내보내지 않는다. */
export function groupDocs(
  categories: readonly ManualCategory[],
  docs: readonly ManualDoc[],
): DocGroup[] {
  return [...categories]
    .sort((a, b) => a.order - b.order)
    .map((category) => ({
      category,
      docs: docs
        .filter((doc) => doc.category === category.slug)
        .sort((a, b) => a.order - b.order),
    }))
    .filter((group) => group.docs.length > 0)
}

/** 목차에 보이는 순서 그대로 펼친 목록. 이전/다음이 이 순서를 따른다. */
export function flatDocs(
  categories: readonly ManualCategory[],
  docs: readonly ManualDoc[],
): ManualDoc[] {
  return groupDocs(categories, docs).flatMap((group) => group.docs)
}

export type Neighbors = { prev: ManualDoc | null; next: ManualDoc | null }

/** 앞뒤 문서. 목차로 돌아가지 않고 순서대로 읽을 수 있게 한다. */
export function neighbors(ordered: readonly ManualDoc[], slug: string): Neighbors {
  const at = ordered.findIndex((doc) => doc.slug === slug)
  if (at === -1) return { prev: null, next: null }
  return { prev: ordered[at - 1] ?? null, next: ordered[at + 1] ?? null }
}

/** 주소의 slug 로 문서를 찾는다. 없으면 맨 앞 문서(모르는 주소로 들어와도 빈 화면이 아니게). */
export function findDoc(ordered: readonly ManualDoc[], slug: string | undefined): ManualDoc | null {
  if (ordered.length === 0) return null
  if (!slug) return ordered[0]
  return ordered.find((doc) => doc.slug === slug) ?? null
}

/** 그 문서가 속한 분류. 목차에서 그 분류만 펼쳐 두는 데 쓴다. */
export function categoryOf(doc: ManualDoc | null): string | null {
  return doc?.category ?? null
}

export function localized(value: Localized, locale: Locale): string {
  return value[locale]
}
